import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RightOutlined } from '@ant-design/icons';
import styles from './Breadcrumb.module.scss';
import { useSecureService } from '../../store/service';

interface BreadcrumbProps {
  pathParts?: string[];
  location?: Location;
  currentEntry?: any;
}

interface BreadcrumbItem {
  name: string;
  path: string;
  displayName: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getBreadcrumb } = useSecureService();
  const isExternalProjectsPage = location.pathname.startsWith('/external-projects');
  const isExternalEntryPath = location.pathname.startsWith('/ext/');
  const isExternalTheme = isExternalProjectsPage || isExternalEntryPath;
  const rootBreadcrumbItem = useMemo(
    () =>
      isExternalTheme
        ? { name: "External Projects", path: "/external-projects", displayName: "External Projects" }
        : { name: "Internal Projects", path: "/internal-projects", displayName: "Internal Projects" },
    [isExternalTheme]
  );
  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItem[]>([
    rootBreadcrumbItem
  ]);
  const [loading, setLoading] = useState(true);
  const lastFetchedPath = useRef<string | null>(null);

  useEffect(() => {
    // URL'den path'i al ve # veya ? sonrasını temizle
    // örn: /ext/b6f1b969-9f5c-4771-a935-527a9472ab60#v1 -> ext/b6f1b969-9f5c-4771-a935-527a9472ab60
    const rawPath = location.pathname.replace(/^\//, '');
    const urlPath = rawPath.split('#')[0].split('?')[0].trim();
    
    // root proje sayfalarındaysak sadece ilgili root breadcrumb'u göster
    const firstSegment = urlPath.split('/')[0];
    if (!urlPath || firstSegment === 'projects' || firstSegment === 'internal-projects' || firstSegment === 'external-projects' || firstSegment === 'dashboard' || firstSegment === 'settings') {
      setBreadcrumbItems([rootBreadcrumbItem]);
      setLoading(false);
      return;
    }
    
    // Aynı path için tekrar fetch yapma
    if (lastFetchedPath.current === urlPath) {
      return;
    }

    const fetchBreadcrumbData = async () => {
      setLoading(true);

      try {
        const response = await getBreadcrumb(urlPath);
        
        if (response.success && response.data && response.data.items && Array.isArray(response.data.items)) {
          const breadcrumbData = response.data.items;
          
          const items: BreadcrumbItem[] = [
            rootBreadcrumbItem,
            ...breadcrumbData.map((item: any) => ({
              name: item.name || '',
              path: item.url ? `/${item.url}` : rootBreadcrumbItem.path,
              displayName: item.name || ''
            }))
          ];
          
          setBreadcrumbItems(items);
          lastFetchedPath.current = urlPath;
        }
      } catch (error) {
        console.error('Error fetching breadcrumb data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBreadcrumbData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, rootBreadcrumbItem]);

  if (loading) {
    return (
      <div className={styles.breadcrumbContainer}>
        <span className={styles.breadcrumbItem}>Loading...</span>
      </div>
    );
  }

  return (
    <div className={`${styles.breadcrumbContainer} ${isExternalTheme ? styles.externalTheme : ''}`}>
      {breadcrumbItems.map((item, index) => (
        <div key={item.path} className={styles.breadcrumbItemWrapper}>
          {index > 0 && <RightOutlined className={styles.breadcrumbSeparator} />}
          <span 
            onClick={() => navigate(item.path)} 
            className={`${styles.breadcrumbItem} ${index === breadcrumbItems.length - 1 ? styles.active : ''}`}
          >
            {item.displayName}
          </span>
        </div>
      ))}
    </div>
  );
};

export default Breadcrumb;
